// Display · Dot
//
// Pure dot indicator with an optional trailing label. No pill chrome — just
// a tonal filled circle + text. Atom — no events, no configurable affordances.
//
// All visual values come from `@ds/tokens/palette`.
// Renamed from StatusDot (was mis-named — already used for provenance /
// source / arbitrary-tone markers, not only "status"). SPECIALIZE decision:
// unique chrome — dot-only shape, no pill border/background — does not fit
// `@ds/display/Pill`.

import type { CSSProperties, ReactNode } from "react";
import {
  DOT_SIZE,
  type DotSize,
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  LINE_HEIGHT,
  SPACE,
  TEXT,
  TONE,
  type ToneName,
} from "@ds/tokens/palette";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export type DotTone = ToneName | "neutral";

export interface DotInput {
  /**
   * Semantic tone driving the dot color. "neutral" maps to `TEXT.muted`
   * (grey, no semantic signal). All other values map to `TONE[tone].base`.
   */
  tone?: DotTone;
  /** Optional trailing label rendered beside the dot. */
  label?: ReactNode;
  /**
   * Dot diameter step from the closed `DOT_SIZE` enum.
   *
   * | Value | px | Typical use |
   * |-------|----|-------------|
   * | "xxs" | 4  | Dense table micro-indicator |
   * | "xs"  | 6  | Compact cell dot (PriceDot) |
   * | "sm"  | 8  | Default — sidebar, KVGrid, PartyCard rows |
   * | "md"  | 10 | Standalone / hero status indicators |
   *
   * Default: `"sm"` (8 px — the historic StatusDot default).
   */
  size?: DotSize;
  /** Additional inline styles on the outer wrapper. */
  style?: CSSProperties;
  /** `data-testid` on the root `<span>`. Atoms do NOT auto-emit inner testids. */
  testId?: string;
}

// ─── Component contract ──────────────────────────────────────────────────
//
// Atom: no Output (no events emitted), no Actions (no configurable
// affordances). The empty interfaces are dropped per @ds convention.

export type DotProps = DotInput;

// ─── Implementation ──────────────────────────────────────────────────────

const DOT_COLOR: Record<DotTone, string> = {
  green:   TONE.green.base,
  red:     TONE.red.base,
  blue:    TONE.blue.base,
  amber:   TONE.amber.base,
  teal:    TONE.teal.base,
  purple:  TONE.purple.base,
  grey:    TONE.grey.base,
  orange:  TONE.orange.base,
  lime:    TONE.lime.base,
  pink:    TONE.pink.base,
  cyan:    TONE.cyan.base,
  stone:   TONE.stone.base,
  neutral: TEXT.muted,
};

export function Dot({
  tone = "neutral",
  label,
  size = "sm",
  style,
  testId,
}: DotProps) {
  const dotColor  = DOT_COLOR[tone];
  const dotSizePx = DOT_SIZE[size];

  return (
    <span
      data-testid={testId}
      style={{
        display:    "inline-flex",
        alignItems: "center",
        gap:        SPACE.sm,
        fontFamily: FONT_FAMILY.sans,
        fontSize:   FONT_SIZE.body,
        fontWeight: FONT_WEIGHT.medium,
        color:      TEXT.primary,
        lineHeight: LINE_HEIGHT.relaxed,
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          width:        dotSizePx,
          height:       dotSizePx,
          borderRadius: "50%",
          background:   dotColor,
          flexShrink:   0,
          display:      "inline-block",
        }}
      />
      {label}
    </span>
  );
}
